<template>
  <div v-if="isBlank" class="min-h-screen bg-theme-bg text-theme-text">
    <router-view />
  </div>

  <div v-else class="min-h-screen bg-theme-bg text-theme-text">
    <!-- Operators hit bugs on these pages too, and theirs are the reports we
         can act on fastest. Same allowlist, same backend; the report records
         which app it came from. -->
    <!-- The reporting flow (panel, picker); its entry point is a sidebar button, not a floating one. -->
    <feedback-launcher v-if="isAuthenticated" :floating="false" />
    <!-- Desktop: fixed rail + contextual sidebar shell. -->
    <div class="fixed inset-y-0 left-0 z-30 hidden lg:block">
      <admin-sidebar />
    </div>

    <div class="lg:pl-64">
      <!-- Compact mobile menu uses the same destination registry as desktop. -->
      <header data-cy="admin-mobile-navigation" class="sticky top-0 z-30 h-11 border-b border-theme-border bg-theme-surface lg:hidden">
        <details ref="mobileMenu" data-cy="admin-mobile-pages" @keydown.esc="closeMobileMenu" @focusout="onMenuFocusOut">
          <summary class="flex h-11 cursor-pointer list-none items-center justify-between gap-3 px-4 text-sm focus-visible:shadow-theme-focus">
            <span class="flex min-w-0 max-w-[calc(100%-132px)] items-center gap-2"><span class="block truncate font-semibold">{{ t('admin.shell.brand') }}</span><acceptance-badge /></span>
            <Icon :path="mdiMenu" size="24" />
          </summary>
          <div class="absolute inset-x-0 top-11 max-h-[calc(100dvh-2.75rem)] overflow-y-auto border-b border-theme-border bg-theme-surface shadow-lg">
            <AdminCauldronRail data-cy="admin-task-groups-mobile" @navigate="closeMobileMenu" />
            <AdminCauldronSidebar :cauldron="activeCauldron" @navigate="closeMobileMenu" />
            <div class="space-y-2 border-t border-theme-border p-3">
              <AppButton v-if="canSubmitFeedback" :label="t('feedback.menu')" :icon="mdiMessageAlertOutline" variant="ghost" tone="neutral" justify="start" block data-test="admin-feedback-open-mobile" @click="closeMobileMenu(); openFeedback()" />
              <AppButton :label="t('admin.shell.sign_out')" :icon="mdiLogout" variant="ghost" tone="neutral" block @click="logout" />
            </div>
          </div>
        </details>
      </header>

      <main class="min-h-[calc(100dvh-2.75rem)] lg:min-h-screen">
        <router-view v-slot="{ Component }">
          <Transition name="onboarding-step" mode="out-in">
            <div :key="activeCauldron?.id || 'home'" class="min-h-[inherit]" data-cy="admin-workspace-content">
              <component :is="Component" />
            </div>
          </Transition>
        </router-view>
      </main>
      <p class="sr-only" role="status" aria-live="polite">{{ activeCauldron ? t(activeCauldron.labelKey) : t('admin.workspace_navigation.dashboard') }}</p>
      <div
        v-if="workspaceArrival"
        :key="workspaceArrival.id"
        class="workspace-arrival pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-theme-bg/80 backdrop-blur-sm lg:left-64"
        aria-hidden="true"
        data-cy="admin-workspace-arrival"
        @animationend.self="workspaceArrival = null"
      >
        <div class="flex flex-col items-center gap-4 rounded-theme-lg border border-theme-border bg-theme-surface px-10 py-8 shadow-theme-dialog">
          <span class="flex h-16 w-16 items-center justify-center rounded-theme-lg ring-8 ring-theme-border/30" :class="workspaceArrival.accentClass">
            <Icon :path="workspaceArrival.icon" size="32" />
          </span>
          <div class="text-center">
            <p class="text-xs font-medium tracking-widest text-theme-text-muted">{{ t('admin.workspace_navigation.workspace') }}</p>
            <p class="mt-1 text-2xl font-semibold text-theme-text">{{ t(workspaceArrival.labelKey) }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import AdminCauldronRail from '@/admin/components/navigation/AdminCauldronRail.vue';
  import AcceptanceBadge from '@/components/ui/feedback/AcceptanceBadge.vue';
  import AdminSidebar from '@/admin/components/navigation/AdminSidebar.vue';
  import AdminCauldronSidebar from '@/admin/components/navigation/AdminCauldronSidebar.vue';
  import { useAdminActiveCauldron } from '@/admin/composables/useAdminActiveCauldron';
  import Icon from '@/components/Icon.vue';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import FeedbackLauncher from '@/features/feedback/components/FeedbackLauncher.vue';
  import { useAuthStore } from '@/stores/useAuthStore';
  import { mdiLogout, mdiMenu, mdiMessageAlertOutline } from '@mdi/js';
  import { openFeedback } from '@/composables/useFeedback';
  import { useFeedbackAccess } from '@/features/feedback/composables/useFeedbackAccess';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute, useRouter } from 'vue-router';

  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();
  const { activeCauldron } = useAdminActiveCauldron();

  // Workspace changes get an arrival cue; ordinary page/filter navigation does not.
  const workspaceArrival = ref(null);
  watch(() => activeCauldron.value?.id, () => {
    workspaceArrival.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? null : activeCauldron.value;
  });

  const mobileMenu = ref(null);
  function onMenuFocusOut(event) {
    if (event.relatedTarget && !mobileMenu.value?.contains(event.relatedTarget)) {
      mobileMenu.value.open = false;
    }
  }
  const { canSubmit: canSubmitFeedback } = useFeedbackAccess();
  function closeMobileMenu() {
    const menu = mobileMenu.value;
    if (!menu) return;
    const returnFocus = menu.contains(document.activeElement);
    menu.open = false;
    if (returnFocus) menu.querySelector('summary')?.focus();
  }
  watch(() => route.path, closeMobileMenu);

  const isBlank = computed(() => route.meta?.blank === true);
  const isAuthenticated = computed(() => authStore.isAuthenticated);

  async function logout() {
    await authStore.logoutUser();
    router.push({ name: 'admin-login' });
  }
</script>

<style scoped>
  .workspace-arrival {
    animation: workspace-arrival calc(3 * var(--motion-duration-slow)) var(--motion-ease-emphasized) both;
  }
  @keyframes workspace-arrival {
    0% { opacity: 0; transform: translateY(var(--motion-travel-md)); }
    18%, 65% { opacity: 1; transform: translateY(0); }
    100% { opacity: 0; transform: translateY(calc(-1 * var(--motion-travel-sm))); }
  }
  @media (prefers-reduced-motion: reduce) {
    .workspace-arrival { animation-name: workspace-arrival-reduced; }
    @keyframes workspace-arrival-reduced {
      from { opacity: 1; }
      to { opacity: 0; }
    }
  }
  @media (max-width: 1023px) {
    /* Dialogs are portaled outside the shell, but share its mobile touch sizing. */
    :global([role="dialog"]) { overflow-wrap: anywhere; }
    :global([role="dialog"] button), :global([role="dialog"] .app-button-theme) { min-height: 44px; min-width: 44px; }
    :global([role="dialog"] input:not([type="checkbox"]):not([type="radio"])), :global([role="dialog"] select), :global([role="dialog"] textarea) { font-size: max(1rem, 16px); }

    :deep(button), :deep([role="button"]), :deep(.app-button-theme) { min-height: 44px; min-width: 44px; }
    :deep(.app-button-theme), :global([role="dialog"] .app-button-theme) { max-width: 100%; height: auto; white-space: normal; overflow-wrap: anywhere; }
    main { overflow-wrap: anywhere; }
    :deep(.control-label), :global([role="dialog"] .control-label) { min-height: 44px; min-width: 44px; }
    :deep(.form-control-grid) { grid-template-columns: minmax(0, 1fr); }
    :deep(.form-field__header) { flex-wrap: wrap; }
    :deep(.form-section__description), :deep(.form-field__label), :deep(.control-label), :deep(main p), :global([role="dialog"] .control-label), :global([role="dialog"] .form-field__label) { overflow-wrap: anywhere; }
    :deep(.section-card__header) { flex-direction: column; align-items: stretch; }
    :deep(.section-card__action) { min-width: 0; max-width: 100%; flex-wrap: wrap; overflow-wrap: anywhere; }
    :deep(a.section-card__action) { min-height: 44px; }
    :deep(.section-card__title) { white-space: normal; overflow-wrap: anywhere; overflow: visible; text-overflow: clip; }
    :deep(input:not([type="checkbox"]):not([type="radio"])), :deep(select), :deep(textarea) { font-size: max(1rem, 16px); }
  }
  @media (max-width: 639px) {
    :global([role="dialog"] .fixed.inset-0.overflow-y-auto > .flex) { padding: min(var(--space-4), 16px); }
    :global([role="dialog"] .max-h-modal) { max-height: calc(100dvh - 32px); }
    :global([role="dialog"] .max-h-modal > .px-5) { padding-inline: min(var(--space-4), 16px); padding-block: min(var(--space-3), 12px); }
    :global([role="dialog"] .max-h-modal > header) { gap: min(var(--space-3), 12px); }
    :global([role="dialog"] .max-h-modal footer), :global([role="dialog"] .max-h-modal footer > span) { gap: min(var(--space-2), 8px); }
    :global([role="dialog"] .max-h-modal footer .py-control-y-md) { padding-block: min(var(--space-control-y-md), 10px); }
    :global([role="dialog"] footer > span), :global([role="dialog"] footer .app-button-theme) { width: 100%; }
  }
  @media (max-width: 639px) and (max-height: 640px) {
    /* Short viewports need one natural scroll area instead of a squeezed form body. */
    :global([role="dialog"] .max-h-modal) { overflow-y: auto; }
    :global([role="dialog"] .max-h-modal > header), :global([role="dialog"] .max-h-modal > footer) { flex-shrink: 0; }
    :global([role="dialog"] .max-h-modal > .flex-1) { flex: none; overflow: visible !important; }
  }
</style>
